import { css, Global, useTheme } from '@emotion/react';
import App, { AppContext, AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useFeature } from '@growthbook/growthbook-react';

import { NativeWebServerProps, useNativeWebProps } from '@updater/native-web';
import { SpinnerFullPage, ThemeProvider } from '@updater/ui-uds';
import { ErrorBoundary } from 'components/error-boundary';
import LivePersonChatScript from 'components/live-person-chat/LivePersonChatScript';
import { LoginRequired } from 'components/login-required';
import { NavigationLayout } from 'components/navigation-layout';
import { RemarketingPixels } from 'components/remarketing-pixels';
import {
  AuthMigrationProvider,
  ConsumerNativeWebBridge,
  RESTClientProvider,
  persistAuth,
} from 'context';
import { ApolloClientSetup } from 'context/apollo-client';
import { persistGuestAuth } from 'context/guest-auth';
import {
  FullstoryProvider,
  GoogleAnalyticsProvider,
  GoogleAnalyticsTracking,
  UIWidgetsProvider,
  UpdaterAuthProvider,
} from 'providers';
import { MovingAppNextPage } from 'types';
import { ServerProps, ServerVariables } from 'utils/ServerProps';
import { RootTrackerProvider } from 'utils/tracker';
import { BlockServerRender } from 'components/block-server-render';
import { GrowthbookAppWrapper } from 'context/growthbook';
import { isGuestPathname } from 'utils/routes';
import { Work_Sans } from 'next/font/google';
import { useSiteBranding } from 'flows/resident-onboarding/hooks/use-site-branding';
import { useInviteToken } from 'flows/core/components/onboarding/common/hooks';
import {
  getWhiteLabelThemeOverrides,
  getDefaultWhiteLabelCustomTheme,
} from 'utils/white-label-theme';
import type { WhiteLabelThemeOverride } from 'types/white-label-theme';
import { WhiteLabelFontLoader } from 'components/white-label-font-loader/WhiteLabelFontLoader';
import { WhiteLabelGoogleFontLoader } from 'components/white-label-font-loader/WhiteLabelGoogleFontLoader';
import { useIsAuthenticated } from '@updater/auth';
import { useQuestionnaire } from 'flows/questionnaire/hooks/use-questionnaire';
import type { SiteBranding } from '@updater/consumer-graph';

const WorkSans = Work_Sans({
  subsets: ['latin', 'latin-ext'],
  weight: ['300', '400', '600'],
  display: 'swap',
  variable: '--font-work-sans',
});

const globalStyles = css`
  :root {
    --font-work-sans: ${WorkSans.style.fontFamily};
  }

  body {
    margin: 0;
  }
`;

// enable auth persistence
persistAuth();
// ...and the guest (public session) equivalent, which shares no storage with it
persistGuestAuth();

// Renders the viewport meta tag with maximum-scale=1.0 to prevent iOS Safari
// from auto-zooming on input focus. Preserves viewport-fit=cover for native
// webview safe-area insets. Must render inside NativeWebServerProps.Provider.
const AppViewport: FC = () => {
  const { safeArea } = useNativeWebProps();
  const content = [
    'width=device-width',
    'initial-scale=1.0',
    'maximum-scale=1.0',
    safeArea && 'viewport-fit=cover',
  ]
    .filter(Boolean)
    .join(', ');
  return (
    <Head>
      <meta name="viewport" content={content} />
    </Head>
  );
};

type MovingAppAppProps = Omit<AppProps, 'Component'> & {
  Component: MovingAppNextPage;
  serverVariables: ServerVariables;
};

/**
 * Inner component that provides white label theme overrides.
 * Must be rendered inside ApolloClientSetup to access siteBranding data.
 *
 * Children are held back until the branding queries settle. Until they do,
 * `themeOverrides` is necessarily empty, so anything rendered paints with the
 * default Updater palette and then repaints in the property's colors when the
 * query lands — the default-blue flash on first load. Showing the UDS spinner
 * in the meantime is safe because `colors.spinner` is not one of the keys
 * `getWhiteLabelThemeOverrides` overrides, so it looks the same before and
 * after branding arrives.
 */
export const WhiteLabelThemedContent: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const isAuthenticated = useIsAuthenticated();
  const inviteToken = useInviteToken();
  const router = useRouter();
  const token = useMemo(() => {
    if (!router?.isReady) return undefined;
    return Array.isArray(router.query.token)
      ? router.query.token[0]
      : router.query.token;
  }, [router?.isReady, router?.query]);

  const { data: siteBrandingData, loading: siteBrandingLoading } =
    useSiteBranding({ inviteToken });
  const { siteBranding: publicSiteBranding, loading: publicBrandingLoading } =
    useQuestionnaire(!isAuthenticated ? token : undefined);

  const themeOverrides = useMemo(() => {
    if (!isAuthenticated && publicSiteBranding) {
      return getWhiteLabelThemeOverrides({
        entityBrandings: [publicSiteBranding],
      } as unknown as SiteBranding);
    }
    return getWhiteLabelThemeOverrides(siteBrandingData?.siteBranding);
  }, [isAuthenticated, publicSiteBranding, siteBrandingData?.siteBranding]);

  /*
   * Branding is settled once the router has parsed the URL — `token` and
   * `inviteToken` both come from the query string and select which branding
   * applies — and neither query is in flight. `useQuestionnaire` reports
   * `loading: false` when it has no token to work with, so this one condition
   * covers the authenticated and public paths alike.
   */
  const brandingSettled =
    !!router?.isReady && !siteBrandingLoading && !publicBrandingLoading;

  /*
   * One-shot, and it has to be: the gate swaps the subtree, so re-closing it
   * would unmount the whole app and take every provider's state with it.
   * `ApolloClientSetup` builds a new ApolloClient whenever the session
   * changes, which resets these queries to `loading: true` — so signing in
   * mid-flow re-closes a derived gate and destroys the flow's state. Only the
   * first paint needs protecting; after that the theme updates in place.
   *
   * setState during render (rather than in an effect) is React's documented
   * way to adjust state from a prop or derived value, and re-renders before
   * the browser paints instead of after.
   */
  const [hasSettled, setHasSettled] = useState(false);
  if (brandingSettled && !hasSettled) {
    setHasSettled(true);
  }

  return (
    <ThemeProvider selfHostedFonts customTheme={themeOverrides}>
      <WhiteLabelFontLoader />
      <WhiteLabelGoogleFontLoader />
      {hasSettled ? children : <SpinnerFullPage />}
    </ThemeProvider>
  );
};

/**
 * TEMPORARY (Phase 1 of the WL-becomes-default rollout, see UPO-3558):
 * once GrowthBook confirms no performance regression, this gate should be
 * removed entirely — fold the `getDefaultWhiteLabelCustomTheme()` fallback
 * directly into `getWhiteLabelThemeOverrides` (utils/white-label-theme.ts) so
 * `WhiteLabelThemedContent` alone always populates `theme.whiteLabelTheme`,
 * then delete this component and its insertion point below. At that point
 * `isWhiteLabeled` in NavigationLayout/BrandedHeader/EssentialLayout is always
 * true and their dead non-WL branches can be deleted too. No GrowthBook
 * context will be needed for any of this anymore.
 *
 * Must be rendered inside GrowthbookAppWrapper (needs GrowthBook context) and
 * inside WhiteLabelThemedContent (needs the real theme, if any, already set).
 */
export const WhiteLabelDefaultLayoutGate: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const theme = useTheme();
  const { on: defaultLayoutEnabled } = useFeature(
    'consumer_app_white_label_default_layout'
  );

  if (theme.whiteLabelTheme || !defaultLayoutEnabled) {
    return <>{children}</>;
  }

  const defaultTheme = getDefaultWhiteLabelCustomTheme();
  const overrides: WhiteLabelThemeOverride = {
    colors: defaultTheme.baseColors,
    whiteLabelTheme: defaultTheme,
  };

  return <ThemeProvider customTheme={overrides}>{children}</ThemeProvider>;
};

const MovingAppWrapper = ({
  Component,
  pageProps,
  serverVariables,
}: MovingAppAppProps) => {
  const router = useRouter();
  const isGuestRoute = isGuestPathname(router.pathname);

  useEffect(() => {
    // eslint-disable-next-line no-console, no-underscore-dangle
    console.log(`appVersion: ${global.__VERSION_STRING__}`);
  }, []);
  const componentWithProvider = Component.provider ? (
    <Component.provider>
      <Component {...pageProps} />
    </Component.provider>
  ) : (
    <Component {...pageProps} />
  );
  return (
    <ThemeProvider>
      <main className={WorkSans.variable}>
        <NativeWebServerProps.Provider value={serverVariables.webview}>
          <ServerProps initialServerVariables={serverVariables}>
            <Head>
              <title>Updater</title>
              {!isGuestRoute && (
                <link rel="shortcut icon" href="/favicon.ico" key="favicon" />
              )}
            </Head>

            <AppViewport />

            <FullstoryProvider env={serverVariables.env} />
            <GoogleAnalyticsProvider
              measurementId={serverVariables.googleAnalytics.measurementId}
            />
            <RemarketingPixels env={serverVariables.env} />
            <UpdaterAuthProvider>
              <ApolloClientSetup>
                <WhiteLabelThemedContent>
                  <RESTClientProvider>
                    <RootTrackerProvider>
                      <AuthMigrationProvider>
                        <UIWidgetsProvider>
                          <Global styles={globalStyles} />

                          <ConsumerNativeWebBridge />

                          <GoogleAnalyticsTracking />

                          <GrowthbookAppWrapper>
                            <WhiteLabelDefaultLayoutGate>
                              <ErrorBoundary>
                                <LivePersonChatScript />
                                {Component.allowUnauthorized ? (
                                  <NavigationLayout>
                                    {componentWithProvider}
                                  </NavigationLayout>
                                ) : (
                                  <BlockServerRender>
                                    <LoginRequired>
                                      <NavigationLayout>
                                        {componentWithProvider}
                                      </NavigationLayout>
                                    </LoginRequired>
                                  </BlockServerRender>
                                )}
                              </ErrorBoundary>
                            </WhiteLabelDefaultLayoutGate>
                          </GrowthbookAppWrapper>
                        </UIWidgetsProvider>
                      </AuthMigrationProvider>
                    </RootTrackerProvider>
                  </RESTClientProvider>
                </WhiteLabelThemedContent>
              </ApolloClientSetup>
            </UpdaterAuthProvider>
          </ServerProps>
        </NativeWebServerProps.Provider>
      </main>
    </ThemeProvider>
  );
};

MovingAppWrapper.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);

  const envSuffix =
    ['prod', 'staging'].includes(process.env.APPLICATION_ENVIRONMENT ?? '') &&
    process.env.POD_NAME?.includes('primary') === false
      ? '-canary'
      : '';
  const props = {
    ...appProps,
    serverVariables: {
      apiBaseUrl: process.env.API_BASE_URL,
      env: `${process.env.APPLICATION_ENVIRONMENT}${envSuffix}`,
      googlePlacesAPIKey: process.env.GOOGLE_PLACES_API_KEY,
      smartyStreetsAPIKey: process.env.SMARTY_STREETS_API_KEY,
      googleAnalytics: {
        measurementId: process.env.GOOGLE_ANALYTICS_MEASUREMENT_ID,
        streamId: process.env.GOOGLE_ANALYTICS_STREAM_ID,
      },
      growthbookAPIHost: process.env.GROWTHBOOK_API_HOST,
      growthbookSDKKey: process.env.GROWTHBOOK_SDK_KEY,
      eventStream: {
        url: process.env.EVENT_STREAM_URL,
        batchUrl: process.env.EVENT_STREAM_BATCH_URL,
        token: process.env.EVENT_STREAM_TOKEN,
      },
      stripeClientKey: process.env.STRIPE_CLIENT_KEY,
      sureStripeClientKey: process.env.SURE_STRIPE_CLIENT_KEY,
      dollyBaseUrl: process.env.DOLLY_BASE_URL,
      sureApi: {
        url: process.env.SURE_API,
        token: process.env.SURE_API_TOKEN,
      },
      tviTokenServiceUrl: process.env.TVI_TOKEN_SERVICE_URL,
      leasingDeskUrl: process.env.LEASING_DESK_URL,
      webview: {
        useragent: appContext.ctx.req?.headers['user-agent'],
        simulated: process.env.SIMULATE_WEBVIEW,
      },
      tokenex: {
        scriptSrc: process.env.TOKENEX_SCRIPT_SRC,
        scriptIntegrity: process.env.TOKENEX_SCRIPT_INTEGRITY,
        iFrameTimeoutInSeconds: parseInt(
          process.env.TOKENEX_TOKENIZE_IFRAME_TIMEOUT_S ?? ''
        ),
        // TODO: remove insurance below once tokenex iframes are implemented
        insurance: {
          vaultId: process.env.TOKENEX_VAULT_ID_INSURANCE,
          apiKey: process.env.TOKENEX_API_KEY_INSURANCE,
        },
        apiUrl: process.env.TOKENEX_API_URL,
      },
    },
  };
  return props;
};

export default MovingAppWrapper;
